
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { Locale, defaultLocale, locales } from './config'

// Default locale loaded statically, others lazy-loaded to reduce bundle size
import enMessages from '@/messages/en.json'

type Messages = typeof enMessages
const messageCache: Partial<Record<Locale, Messages>> = { en: enMessages }

async function loadMessages(locale: Locale): Promise<Messages> {
  if (messageCache[locale]) return messageCache[locale]!
  const mod = await import(`@/messages/${locale}.json`)
  messageCache[locale] = mod.default
  return mod.default
}

interface LocaleContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
  messages: typeof enMessages
}

const LocaleContext = createContext<LocaleContextType | null>(null)

const LOCALE_STORAGE_KEY = 'app-locale'

function getStoredLocale(): Locale {
  if (typeof window === 'undefined') return defaultLocale

  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (stored && locales.includes(stored as Locale)) {
    return stored as Locale
  }

  // Try to detect from browser
  const browserLang = navigator.language.split('-')[0]
  if (locales.includes(browserLang as Locale)) {
    return browserLang as Locale
  }

  return defaultLocale
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale)
  const [currentMessages, setCurrentMessages] = useState<Messages>(enMessages)
  const [isHydrated, setIsHydrated] = useState(false)

  // Hydrate locale from storage on mount
  useEffect(() => {
    const stored = getStoredLocale()
    setLocaleState(stored)
    loadMessages(stored).then(setCurrentMessages)
    setIsHydrated(true)
  }, [])

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale)
    document.documentElement.lang = newLocale
    loadMessages(newLocale).then(setCurrentMessages)
  }, [])

  // Translation function
  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const keys = key.split('.')
    let value: any = currentMessages

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        return key
      }
    }

    if (typeof value !== 'string') {
      return key
    }

    if (params) {
      return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
        return params[paramKey]?.toString() ?? `{${paramKey}}`
      })
    }

    return value
  }, [currentMessages])

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    messages: currentMessages
  }), [locale, setLocale, t, currentMessages])

  if (!isHydrated) {
    return (
      <LocaleContext.Provider value={{ ...value, locale: defaultLocale, messages: enMessages }}>
        {children}
      </LocaleContext.Provider>
    )
  }

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  )
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return {
    locale: context.locale,
    setLocale: context.setLocale
  }
}

export function useTranslations(namespace?: string) {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useTranslations must be used within a LocaleProvider')
  }

  const t = useCallback((key: string, params?: Record<string, string | number>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key
    return context.t(fullKey, params)
  }, [context.t, namespace])

  return t
}
